import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

//components
import { Alert } from 'components/common/Alert';
import StaffCard from 'components/main/Staff/Add/StaffCard';

//interfaces , constants , validationSchema, graphql
import { updateStaffSchema } from 'validationSchema';
import { useUpdateOrgStaffInfoMutation } from 'generated/graphql';
import { EditOrgStaffFormProps, EditStaffFormType } from 'interfaces';
import { HTTP_STATUS, updateStaffInitialValues } from 'constants/index';

const EditOrgStaffForm: FC<EditOrgStaffFormProps> = ({
  loading: getLoading,
  id,
  organizationId,
  user: staff,
}) => {
  const navigate = useNavigate();

  const methods = useForm<EditStaffFormType>({
    defaultValues: updateStaffInitialValues,
    resolver: yupResolver(updateStaffSchema),
  });

  const { handleSubmit, setValue } = methods;

  const [updateOrgStaffInfo, { loading: updateOrgStaffLoading }] = useUpdateOrgStaffInfoMutation({
    onCompleted: (data) => {
      const { updateOrgStaffInfo } = data;
      const { response } = updateOrgStaffInfo || {};
      const { status, message } = response || {};
      if (message && status === HTTP_STATUS.SUCCESS) {
        Alert.success(message);
        navigate(-1);
      } else {
        Alert.warning(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const onSubmit: SubmitHandler<EditStaffFormType> = async (data) => {
    const { firstName, lastName, email, phone, role } = data;
    if (id && organizationId) {
      await updateOrgStaffInfo({
        variables: {
          updateOrgStaffInfoInput: {
            id,
            email,
            organizationId,
            lastName,
            firstName,
            staffRole: role,
            ...(phone && { phone }),
          },
        },
      });
    }
  };

  const setValues = useCallback(() => {
    const { firstName, lastName, email, phone, staffRole } = staff || {};
    setValue('email', email ?? '');
    setValue('firstName', firstName ?? '');
    setValue('lastName', lastName ?? '');
    setValue('role', staffRole ?? '');
    setValue('phone', phone ?? '');
  }, [setValue, staff]);

  useEffect(() => {
    staff && setValues();
  }, [setValues, staff]);

  const loading = getLoading || updateOrgStaffLoading;

  return (
    <Grid container spacing={3} mt={1}>
      <Grid item xs={12} xl={7}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StaffCard loading={loading} isEdit editableEmail />
          </form>
        </FormProvider>
      </Grid>
    </Grid>
  );
};

export default EditOrgStaffForm;
